/**
 * Catálogo de activos: tipos, fabricantes y modelos.
 *
 * El acta declara **marca y modelo de cada equipo** (anexo II §5.b). Las
 * tablas `manufacturer` y `equipment_model` existían desde la migración 0008
 * y nadie insertaba en ellas, así que la validación del acta reclamaba
 * «falta marca, modelo del equipo» sin que hubiera forma de darlos de alta.
 *
 * Los tipos de equipo no se crean aquí: los carga el motor normativo con la
 * versión del RIPCI. Se listan porque el alta de equipo y la de modelo los
 * necesitan, y hasta ahora había que saberse los códigos de memoria.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { DetailShell, type Tab } from './detail/detail-shell.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';

/** Del listado que ya existía: `GET /v1/regulation/equipment-types`. */
interface EquipmentType {
  id: string;
  code: string;
  name: string;
  fire_system_code: string;
  fire_system_name: string;
  requires_serial: boolean;
}

interface Manufacturer {
  id: string;
  code: string;
  name: string;
  website: string | null;
}

interface Model {
  id: string;
  code: string;
  name: string;
  manufacturer_name: string | null;
  equipment_type_code: string;
  equipment_type_name: string;
  manufacturer_life_years: number | null;
  certified_durability_years: number | null;
}

export function AssetCatalogScreen(): React.ReactNode {
  const { api, user } = useSession();

  const [types, setTypes] = useState<EquipmentType[]>([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [mCode, setMCode] = useState('');
  const [mName, setMName] = useState('');

  const [modCode, setModCode] = useState('');
  const [modName, setModName] = useState('');
  const [modType, setModType] = useState('');
  const [modManufacturer, setModManufacturer] = useState('');
  const [modLife, setModLife] = useState('');

  const canCreate = hasPermission(user, 'assets.create');

  const reload = useCallback(async () => {
    const [t, f, m] = await Promise.all([
      api.get<{ items: EquipmentType[] }>('/regulation/equipment-types').catch(() => ({ items: [] })),
      api.get<{ items: Manufacturer[] }>('/catalog/manufacturers').catch(() => ({ items: [] })),
      api.get<{ items: Model[] }>('/catalog/models').catch(() => ({ items: [] })),
    ]);
    setTypes(t.items);
    setManufacturers(f.items);
    setModels(m.items);
  }, [api]);

  useEffect(() => {
    reload()
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar el catálogo.');
      })
      .finally(() => setLoading(false));
  }, [reload]);

  async function createManufacturer(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/catalog/manufacturers', { code: mCode, name: mName });
      setNotice(`Fabricante ${mName} añadido.`);
      setMCode('');
      setMName('');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear el fabricante.');
    } finally {
      setBusy(false);
    }
  }

  async function createModel(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/catalog/models', {
        code: modCode,
        name: modName,
        equipmentTypeCode: modType,
        ...(modManufacturer === '' ? {} : { manufacturerId: modManufacturer }),
        ...(modLife === '' ? {} : { manufacturerLifeYears: Number(modLife) }),
      });
      setNotice(`Modelo ${modName} añadido.`);
      setModCode('');
      setModName('');
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear el modelo.');
    } finally {
      setBusy(false);
    }
  }

  const tabs: Tab[] = [
    {
      id: 'modelos',
      label: 'Modelos',
      count: models.length,
      render: () => (
        <>
          <p className="screen__note">
            El acta declara marca y modelo de cada equipo (anexo II §5.b). Un equipo sin modelo
            asignado impide que su acta sea conforme.
          </p>

          {models.length > 0 && (
            <div className="scroller">
              <table className="table__grid table__grid--compact">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Modelo</th>
                    <th>Fabricante</th>
                    <th>Tipo de equipo</th>
                    <th>Vida útil</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((m) => (
                    <tr key={m.id}>
                      <td>{m.code}</td>
                      <td>{m.name}</td>
                      <td>{m.manufacturer_name ?? '—'}</td>
                      <td>{m.equipment_type_name}</td>
                      <td>
                        {m.manufacturer_life_years ? `${m.manufacturer_life_years} años` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canCreate && (
            <div className="form">
              <FormSection title="Nuevo modelo">
                <TextField label="Código" value={modCode} onChange={setModCode} required />
                <TextField label="Nombre" value={modName} onChange={setModName} required />
                <SelectField
                  label="Tipo de equipo"
                  value={modType}
                  onChange={setModType}
                  options={types.map((t) => ({ value: t.code, label: `${t.name} · ${t.fire_system_name}` }))}
                  emptyLabel="Seleccione…"
                  required
                />
                <SelectField
                  label="Fabricante"
                  value={modManufacturer}
                  onChange={setModManufacturer}
                  options={manufacturers.map((f) => ({ value: f.id, label: f.name }))}
                  emptyLabel="Sin fabricante"
                />
                <TextField
                  label="Vida útil del fabricante (años)"
                  type="number"
                  value={modLife}
                  onChange={setModLife}
                  help="El programa del fabricante AÑADE operaciones al mínimo legal; nunca lo reduce (anexo II §1)."
                />
                <button
                  className="button button--primary"
                  type="button"
                  disabled={busy || modCode === '' || modName === '' || modType === ''}
                  onClick={() => void createModel()}
                >
                  {busy ? 'Guardando…' : 'Crear modelo'}
                </button>
              </FormSection>
            </div>
          )}
        </>
      ),
    },
    {
      id: 'fabricantes',
      label: 'Fabricantes',
      count: manufacturers.length,
      render: () => (
        <>
          {manufacturers.length > 0 && (
            <div className="scroller">
              <table className="table__grid table__grid--compact">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                  </tr>
                </thead>
                <tbody>
                  {manufacturers.map((f) => (
                    <tr key={f.id}>
                      <td>{f.code}</td>
                      <td>{f.name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canCreate && (
            <div className="form">
              <FormSection title="Nuevo fabricante">
                <TextField label="Código" value={mCode} onChange={setMCode} required />
                <TextField label="Nombre" value={mName} onChange={setMName} required />
                <button
                  className="button button--primary"
                  type="button"
                  disabled={busy || mCode === '' || mName === ''}
                  onClick={() => void createManufacturer()}
                >
                  {busy ? 'Guardando…' : 'Crear fabricante'}
                </button>
              </FormSection>
            </div>
          )}
        </>
      ),
    },
    {
      id: 'tipos',
      label: 'Tipos de equipo',
      count: types.length,
      render: () => (
        <>
          <p className="screen__note">
            Los carga el motor normativo con la versión del RIPCI: no se crean a mano. Son los
            códigos que pide el alta de equipo.
          </p>
          <div className="scroller">
            <table className="table__grid table__grid--compact">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Tipo</th>
                  <th>Sistema</th>
                  <th>Nº de serie</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id}>
                    <td>
                      <code>{t.code}</code>
                    </td>
                    <td>{t.name}</td>
                    <td>{t.fire_system_name}</td>
                    <td>{t.requires_serial ? 'obligatorio' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}
      <DetailShell
        title="Catálogo de activos"
        subtitle={`${types.length} tipos · ${manufacturers.length} fabricantes · ${models.length} modelos`}
        tabs={tabs}
        loading={loading}
        error={error}
      />
    </>
  );
}
